import React, { useEffect, useCallback, useContext, useRef } from "react";
import { useUnifiedService } from "../../../utilities/contexts/UnifiedServiceContext";
import { ServiceStatus, ServiceStep } from "../../../utilities/types/unified-services";
import AppContext from "../../../utilities/contexts/appContext";
import SdkContext from "../../../utilities/contexts/sdk/sdkContext";
import { useLocation } from "react-router-dom";
import { addRefIdToPayload } from "../../../utilities/helpers/refIdHelper";

const GarmentMeasurementProcessing: React.FC = () => {
  const { garmentMeasurement, serviceInterface, serviceConfig } = useUnifiedService();
  const { setStatus, setCanProceedToNextStep, setCurrentStep } = serviceInterface;
  const appContext = useContext(AppContext);
  const sdkContext = useContext(SdkContext);
  const location = useLocation();
  const isProcessingRef = useRef(false);
  const hasStartedRef = useRef(false);

  const isSDK = location.pathname.includes("/sdk/");
  // If requestId exists, always use SDK Token (client-specific request)
  // Otherwise: Main Token (if logged in) > SDK Token (fallback)
  const authToken = isSDK ? (sdkContext.requestId ? sdkContext.token : appContext.token || sdkContext.token) : appContext.token;

  const processImages = useCallback(async () => {
    if (isProcessingRef.current || hasStartedRef.current) {
      return;
    }

    const validImages = garmentMeasurement.images.filter((img) => img.isValid && img.base64);

    if (validImages.length === 0) {
      return;
    }

    isProcessingRef.current = true;
    hasStartedRef.current = true;

    try {
      setStatus(ServiceStatus.LOADING);

      const results = [];

      for (let index = 0; index < validImages.length; index++) {
        const image = validImages[index];

        try {
          garmentMeasurement.setProcessingStatus(index, "processing");

          const payload = addRefIdToPayload({
            Image: image.base64!,
          });

          const response = await fetch(serviceConfig.api.endpoint, {
            method: serviceConfig.api.method,
            headers: {
              "Content-Type": "application/json",
              ...(serviceConfig.api.requiresAuth && {
                Authorization: `Bearer ${authToken || ""}`,
              }),
            },
            body: JSON.stringify(payload),
          });

          if (!response.ok) {
            garmentMeasurement.setProcessingStatus(index, "error");
            console.error(`API Error for image ${index + 1}: ${response.status}`);
            continue;
          }

          const data = await response.json();

          if (!data.ok || data.status_code !== 200) {
            garmentMeasurement.setProcessingStatus(index, "error");
            console.error(`Service error for image ${index + 1}:`, data.message);
            continue;
          }

          garmentMeasurement.setProcessingStatus(index, "success");

          results.push({
            imageIndex: index,
            result: data.data,
          });
        } catch (imageError: any) {
          garmentMeasurement.setProcessingStatus(index, "error");
          console.error(`Processing error for image ${index + 1}:`, imageError);
        }
      }

      if (results.length > 0) {
        garmentMeasurement.setAllMeasurements(results.map((r) => r.result));
        garmentMeasurement.setResult(results[0].result);

        setStatus(ServiceStatus.SUCCESS);
        setCanProceedToNextStep(true);

        setTimeout(() => {
          setCurrentStep(ServiceStep.RESULTS);
        }, 1500);
      } else {
        setStatus(ServiceStatus.ERROR);
        setCanProceedToNextStep(true);

        setTimeout(() => {
          setCurrentStep(ServiceStep.RESULTS);
        }, 1500);
      }
    } catch (error: any) {
      validImages.forEach((_, index) => {
        garmentMeasurement.setProcessingStatus(index, "error");
      });

      setStatus(ServiceStatus.ERROR);
      setCanProceedToNextStep(true);

      setTimeout(() => {
        setCurrentStep(ServiceStep.RESULTS);
      }, 1500);
    } finally {
      isProcessingRef.current = false;
    }
  }, [garmentMeasurement, serviceConfig, authToken]);

  useEffect(() => {
    const validImages = garmentMeasurement.images.filter((img) => img.isValid && img.base64);

    if (validImages.length > 0 && !hasStartedRef.current && !isProcessingRef.current) {
      processImages();
    }
  }, [garmentMeasurement.images, processImages]);

  const validImages = garmentMeasurement.images.filter((img) => img.isValid && img.base64);
  const totalImages = validImages.length;

  const processedImages = garmentMeasurement.processingStatus ? Object.values(garmentMeasurement.processingStatus).filter((status) => status === "success").length : 0;
  const failedImages = garmentMeasurement.processingStatus ? Object.values(garmentMeasurement.processingStatus).filter((status) => status === "error").length : 0;
  const processingImages = garmentMeasurement.processingStatus ? Object.values(garmentMeasurement.processingStatus).filter((status) => status === "processing").length : 0;

  const progress = totalImages > 0 ? Math.round(((processedImages + failedImages) / totalImages) * 100) : 0;

  return (
    <div className="flex flex-col items-center w-full gap-4 md:gap-16">
      <div className="flex flex-col items-center w-full gap-4">
        <span className="font-bold text-center capitalize text-secondary">Processing images...</span>

        <div className="w-full max-w-md px-4">
          <div className="flex flex-col gap-4 p-4 border border-gray-200 rounded-lg">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Progress</span>
              <span className="text-xs font-medium text-gray-800">{progress}%</span>
            </div>

            <div className="w-full h-2 overflow-hidden bg-gray-100 rounded-full">
              <div className="h-full transition-all duration-300 ease-in-out bg-primary" style={{ width: `${progress}%` }}></div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col">
                <span className="text-xs text-gray-500">Total Images</span>
                <span className="font-medium text-gray-700">{totalImages}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-xs text-gray-500">Processed</span>
                <span className="font-medium text-gray-700">{processedImages}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-xs text-gray-500">Processing</span>
                <span className="font-medium text-gray-700">{processingImages}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-xs text-red-500">Failed</span>
                <span className="font-medium text-red-600">{failedImages}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full max-w-5xl px-4">
          <div className="flex flex-col w-full border border-gray-200 divide-y divide-gray-200 rounded-lg">
            <h3 className="p-4 text-secondary">Images Status</h3>
            <div className="p-4">
              <GarmentMeasurementImageList />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const GarmentMeasurementImageList: React.FC = () => {
  const { garmentMeasurement } = useUnifiedService();

  const validImages = garmentMeasurement.images.filter((img) => img.isValid && img.base64);

  if (validImages.length === 0) {
    return (
      <div className="flex items-center justify-center p-4 my-4 border border-gray-200 rounded-lg">
        <p className="text-gray-500">No images uploaded</p>
      </div>
    );
  }

  const getStatusIcon = (index: number) => {
    const status = garmentMeasurement.processingStatus?.[index];

    if (status === "processing") {
      return (
        <div className="absolute flex items-center justify-center bg-white border border-gray-200 rounded-full size-6 top-2 right-2">
          <div className="w-4 h-4 border-2 border-blue-500 rounded-full border-t-transparent animate-spin" />
        </div>
      );
    } else if (status === "success") {
      return (
        <div className="absolute flex items-center justify-center bg-white border border-gray-200 rounded-full size-6 top-2 right-2">
          <div className="flex items-center justify-center w-4 h-4 text-xs text-white bg-green-500 rounded-full">✓</div>
        </div>
      );
    } else if (status === "error") {
      return (
        <div className="absolute flex items-center justify-center bg-white border border-gray-200 rounded-full size-6 top-2 right-2">
          <div className="flex items-center justify-center w-4 h-4 text-xs text-white bg-red-500 rounded-full">✕</div>
        </div>
      );
    } else if (status === "pending") {
      return (
        <div className="absolute flex items-center justify-center px-2 py-1 text-xs leading-3 bg-white border border-gray-200 rounded-full top-2 right-2">
          <span className="text-gray-500">Pending</span>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 place-items-center">
      {validImages.map((image, index) => {
        const hasError = !!image.error;

        return (
          <div key={index} className="relative transition-all border border-gray-200 rounded-lg w-full max-w-[15rem] overflow-hidden">
            <div className="relative grid place-content-center w-full h-[20rem]">
              <img src={image.preview || `data:image/png;base64,${image.base64}`} alt={`Uploaded garment ${index + 1}`} className="object-contain w-full h-full" />
            </div>
            <div className="absolute grid text-xs leading-3 text-gray-600 bg-white border border-gray-200 rounded-full size-6 place-content-center top-2 left-2">{index + 1}</div>
            {getStatusIcon(index)}

            {hasError && garmentMeasurement.processingStatus?.[index] === "error" && <div className="absolute bottom-0 left-0 right-0 p-2 text-xs text-white bg-red-500">{image.error}</div>}
          </div>
        );
      })}
    </div>
  );
};

export default GarmentMeasurementProcessing;
